/**
 * The ONE shared wrapper every image-variant consumer uses. Owns the pieces
 * each component would otherwise hand-roll divergently: the resolve effect,
 * the original-first/swap-async state, the stale-URL race guard, the
 * onError swap-back, and the metric recording.
 *
 * Contract with consumers:
 * - `src` is the ORIGINAL url until a variant RESOLVES AND PRELOADS — first
 *   paint never waits on a storage probe, and the swap never flashes the
 *   fallback (the preloaded bytes are already in the browser cache).
 * - Wire `onError` onto the image as a belt-and-suspenders swap-back.
 *   Metrics: tokened outcomes are recorded inside the resolver; public
 *   outcomes are recorded by the preload (its load/error result is the only
 *   signal the public branch has — its URLs are built without a round-trip).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { ImageVariantSize } from "@lemonadetraining/contracts";
import type { ImageSurface } from "../constants/imageAnalyticsEvents";
import { recordVariantOutcome } from "../utils/imageAnalytics";
import { resolveImageVariant } from "../utils/imageVariants";

export interface ImageVariantHandle {
  /** What to render right now: the original at first, the variant once
   * preloaded (the swap always hits the browser cache — no fallback flash). */
  src: string | null | undefined;
  /** Wire onto the image: swaps a failed variant back to the original. */
  onError: () => void;
}

export function useImageVariant(
  originalUrl: string | null | undefined,
  size: ImageVariantSize,
  surface: ImageSurface
): ImageVariantHandle {
  const [variantSrc, setVariantSrc] = useState<string | null>(null);
  /* Ref mirror of variantSrc so the stable callbacks can read the current
   * value without putting side effects inside a setState updater (updaters
   * must stay pure — StrictMode double-invokes them). */
  const variantSrcRef = useRef<string | null>(null);
  /* One metric tick per resolved variant render — guards double-fires from
   * repeated load/error events on the same element. */
  const reportedRef = useRef(false);

  useEffect(() => {
    variantSrcRef.current = null;
    setVariantSrc(null);
    reportedRef.current = false;
    if (!originalUrl) return undefined;
    let alive = true;
    void resolveImageVariant(originalUrl, size, surface).then((resolved) => {
      if (!alive || resolved === originalUrl) return;
      /* Public variant URLs: the GCS form in production, or the
       * emulator-REST form (alt=media, no token=) in the E2E tier.
       * Tokened getDownloadURL output always carries token=. */
      const isPublicVariantUrl =
        resolved.startsWith("https://storage.googleapis.com/") ||
        (resolved.includes("/v0/b/") && !resolved.includes("token="));
      /* TOKENED outcomes are recorded INSIDE the resolver, at resolve time —
       * a tokened `hit` means "resolved", not "rendered". Mark this element
       * reported IMMEDIATELY so neither the preload's onerror nor the
       * element's onError can add a `fallback` on top: that hit+fallback
       * double count is exactly what inflates the variant_coverage signal
       * the cutover dashboard reads. (Mirrors the mobile hook.) */
      if (!isPublicVariantUrl) reportedRef.current = true;
      /* PRELOAD before swapping: assigning a new src makes the avatar
       * primitive unmount its img until the new bytes load — swapping
       * directly would flash the fallback over an already-visible photo.
       * Loading into a detached Image first means the swap hits the browser
       * cache and the upgrade is genuinely in place. The preload outcome is
       * also the public branch's ONLY miss/hit signal (its URLs are built
       * without a round-trip). */
      const probe = new Image();
      probe.onload = () => {
        if (!alive) return;
        if (isPublicVariantUrl && !reportedRef.current) {
          reportedRef.current = true;
          recordVariantOutcome(surface, "hit");
        }
        variantSrcRef.current = resolved;
        setVariantSrc(resolved);
      };
      probe.onerror = () => {
        if (!alive || reportedRef.current) return;
        reportedRef.current = true;
        recordVariantOutcome(surface, "fallback");
      };
      probe.src = resolved;
    });
    return () => {
      alive = false;
    };
  }, [originalUrl, size, surface]);

  /* Belt-and-suspenders: the preload should catch a bad variant before it
   * ever renders, but if a swapped variant still errors (e.g. evicted from
   * cache and deleted server-side), swap back to the original. */
  const onError = useCallback(() => {
    if (variantSrcRef.current && !reportedRef.current) {
      reportedRef.current = true;
      recordVariantOutcome(surface, "fallback");
    }
    variantSrcRef.current = null;
    setVariantSrc(null);
  }, [surface]);

  return { src: variantSrc ?? originalUrl, onError };
}
